import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertCircle, Bug, Send, ArrowLeft } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

interface BugReportFormData {
  loginStatus: string;
  userRole: string;
  module: string;
  issueDescription: string;
  stepsToReproduce: string;
  deviceInfo: {
    deviceType: string;
    operatingSystem: string;
    browser: string;
    browserVersion: string;
    screenResolution: string;
  };
  priority: string;
  bugType: string;
}

export default function BugReport() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();
  const { success, error } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<BugReportFormData>({
    loginStatus: 'with_login',
    userRole: user?.role || 'aspirant',
    module: '',
    issueDescription: '',
    stepsToReproduce: '',
    deviceInfo: {
      deviceType: '',
      operatingSystem: '',
      browser: '',
      browserVersion: '',
      screenResolution: ''
    },
    priority: 'Medium',
    bugType: 'Bug'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Immediate redirect if not authenticated and not loading
  if (!isLoading && !isAuthenticated) {
    error('Authentication Required', 'Please log in to report bugs or suggestions.');
    navigate('/login');
    return null;
  }

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Auto-detect device information when component mounts
  useEffect(() => {
    const detectDeviceInfo = () => {
      const userAgent = navigator.userAgent;
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      
      // Detect device type
      let deviceType = 'Desktop';
      if (/Mobile|Android|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(userAgent)) {
        deviceType = /iPad|Tablet/i.test(userAgent) ? 'Tablet' : 'Mobile';
      } else if (screenWidth <= 768) {
        deviceType = 'Mobile';
      } else if (screenWidth <= 1024) {
        deviceType = 'Tablet';
      }

      // Detect operating system
      let operatingSystem = 'Windows';
      if (/Mac OS|macOS/i.test(userAgent)) operatingSystem = 'MacOS';
      else if (/Linux/i.test(userAgent)) operatingSystem = 'Linux';
      else if (/Android/i.test(userAgent)) operatingSystem = 'Android';
      else if (/iOS|iPhone|iPad/i.test(userAgent)) operatingSystem = 'iOS';

      // Detect browser
      let browser = 'Other';
      if (/Chrome/i.test(userAgent) && !/Edge/i.test(userAgent)) browser = 'Chrome';
      else if (/Firefox/i.test(userAgent)) browser = 'Firefox';
      else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) browser = 'Safari';
      else if (/Edge/i.test(userAgent)) browser = 'Edge';
      else if (/Opera/i.test(userAgent)) browser = 'Opera';

      // Get browser version
      const browserVersion = userAgent.match(/(?:Chrome|Firefox|Safari|Edge|Opera)\/([0-9.]+)/)?.[1] || 'Unknown';

      setFormData(prev => ({
        ...prev,
        userRole: user?.role || 'aspirant', // Update user role from auth context
        deviceInfo: {
          ...prev.deviceInfo,
          deviceType,
          operatingSystem,
          browser,
          browserVersion,
          screenResolution: `${screenWidth}x${screenHeight}`
        }
      }));
    };

    detectDeviceInfo();
  }, []); // Only run once when component mounts

  const modules = [
    { value: 'registration', label: 'Registration' },
    { value: 'login', label: 'Login' },
    { value: 'events', label: 'Events' },
    { value: 'jobs', label: 'Jobs' },
    { value: 'profile', label: 'Profile' },
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'mentorship', label: 'Mentorship' },
    { value: 'admin_panel', label: 'Admin Panel' },
    { value: 'general', label: 'General' },
    { value: 'other', label: 'Other' }
  ];

  const priorities = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Critical', label: 'Critical' }
  ];

  const bugTypes = [
    { value: 'Bug', label: 'Bug' },
    { value: 'Feature Request', label: 'Feature Request' },
    { value: 'Enhancement', label: 'Enhancement' },
    { value: 'UI/UX Issue', label: 'UI/UX Issue' },
    { value: 'Performance Issue', label: 'Performance Issue' }
  ];

  const deviceTypes = [
    { value: 'Mobile', label: 'Mobile' },
    { value: 'Tablet', label: 'Tablet' },
    { value: 'Laptop', label: 'Laptop' },
    { value: 'Desktop', label: 'Desktop' }
  ];

  const operatingSystems = [
    { value: 'Android', label: 'Android' },
    { value: 'iOS', label: 'iOS' },
    { value: 'Windows', label: 'Windows' },
    { value: 'MacOS', label: 'MacOS' },
    { value: 'Linux', label: 'Linux' }
  ];

  const browsers = [
    { value: 'Chrome', label: 'Chrome' },
    { value: 'Firefox', label: 'Firefox' },
    { value: 'Safari', label: 'Safari' },
    { value: 'Edge', label: 'Edge' },
    { value: 'Opera', label: 'Opera' },
    { value: 'Other', label: 'Other' }
  ];

  const handleInputChange = (field: string, value: string) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof BugReportFormData] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.module) newErrors.module = 'Module is required';
    if (!formData.issueDescription.trim()) newErrors.issueDescription = 'Issue description is required';
    else if (formData.issueDescription.length < 10) newErrors.issueDescription = 'Issue description must be at least 10 characters';
    else if (formData.issueDescription.length > 2000) newErrors.issueDescription = 'Issue description cannot exceed 2000 characters';

    if (!formData.stepsToReproduce.trim()) newErrors.stepsToReproduce = 'Steps to reproduce is required';
    else if (formData.stepsToReproduce.length < 10) newErrors.stepsToReproduce = 'Steps to reproduce must be at least 10 characters';
    else if (formData.stepsToReproduce.length > 1000) newErrors.stepsToReproduce = 'Steps to reproduce cannot exceed 1000 characters';

    if (!formData.deviceInfo.deviceType) newErrors['deviceInfo.deviceType'] = 'Device type is required';
    if (!formData.deviceInfo.operatingSystem) newErrors['deviceInfo.operatingSystem'] = 'Operating system is required';
    if (!formData.deviceInfo.browser) newErrors['deviceInfo.browser'] = 'Browser is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      error(
        'Validation Error',
        'Please fix all errors before submitting.'
      );
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/bug-reports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        const ticketId = data.data?.bugId || 'Unknown';
        success(
          'Bug Report Submitted Successfully!',
          `Your ticket ${ticketId} has been created. Our team will review it soon.`
        );

        // Navigate to tickets page to view the submitted ticket
        setTimeout(() => {
          navigate('/support/tickets');
        }, 2000);
      } else {
        error(
          'Submission Failed',
          data.message || 'Failed to submit bug report. Please try again.'
        );
      }
    } catch (err) {
      console.error('Bug report submission error:', err);
      error(
        'Network Error',
        'Unable to submit bug report. Please check your connection.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/support')}
                className="text-gray-600 hover:text-secondary"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Support Center
              </Button>
            </div>
            <CardTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-red-500" />
              Report Bug or Suggestion
            </CardTitle>
            <p className="text-gray-600">
              Help us improve by reporting bugs or suggesting new features. Your feedback is valuable to us.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* User Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="relative z-40">
                  <Label htmlFor="loginStatus">Login Status</Label>
                  <Select value={formData.loginStatus} onValueChange={(value) => handleInputChange('loginStatus', value)}>
                    <SelectTrigger className="bg-surface">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface z-50">
                      <SelectItem value="with_login">With Login</SelectItem>
                      <SelectItem value="without_login">Without Login</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="relative z-40">
                  <Label htmlFor="userRole">Role</Label>
                  <Select value={formData.userRole} onValueChange={(value) => handleInputChange('userRole', value)}>
                    <SelectTrigger className="bg-surface">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface z-50">
                      <SelectItem value="aspirant">Aspirant</SelectItem>
                      <SelectItem value="mentor">Mentor</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Issue Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="relative z-30">
                  <Label htmlFor="module">Module *</Label>
                  <Select value={formData.module} onValueChange={(value) => handleInputChange('module', value)}>
                    <SelectTrigger className="bg-surface">
                      <SelectValue placeholder="Select module" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface z-50">
                      {modules.map(module => (
                        <SelectItem key={module.value} value={module.value}>
                          {module.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.module && <p className="text-red-500 text-sm mt-1">{errors.module}</p>}
                </div>

                <div className="relative z-30">
                  <Label htmlFor="bugType">Report Type</Label>
                  <Select value={formData.bugType} onValueChange={(value) => handleInputChange('bugType', value)}>
                    <SelectTrigger className="bg-surface">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface z-50">
                      {bugTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="relative z-20">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => handleInputChange('priority', value)}>
                  <SelectTrigger className="bg-surface">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface z-50">
                    {priorities.map(priority => (
                      <SelectItem key={priority.value} value={priority.value}>
                        {priority.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Issue Description */}
              <div>
                <Label htmlFor="issueDescription">Issue Description *</Label>
                <Textarea
                  id="issueDescription"
                  placeholder="Describe the issue in detail..."
                  value={formData.issueDescription}
                  onChange={(e) => handleInputChange('issueDescription', e.target.value)}
                  rows={4}
                  className={errors.issueDescription ? 'border-red-500' : ''}
                />
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>{errors.issueDescription && <span className="text-red-500">{errors.issueDescription}</span>}</span>
                  <span>{formData.issueDescription.length}/2000</span>
                </div>
              </div>

              {/* Steps to Reproduce */}
              <div>
                <Label htmlFor="stepsToReproduce">Steps to Reproduce *</Label>
                <Textarea
                  id="stepsToReproduce"
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                  value={formData.stepsToReproduce}
                  onChange={(e) => handleInputChange('stepsToReproduce', e.target.value)}
                  rows={3}
                  className={errors.stepsToReproduce ? 'border-red-500' : ''}
                />
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>{errors.stepsToReproduce && <span className="text-red-500">{errors.stepsToReproduce}</span>}</span>
                  <span>{formData.stepsToReproduce.length}/1000</span>
                </div>
              </div>

              {/* Device Information */}
              <div>
                <Label className="text-base font-semibold">Device & Browser Information</Label>
                <div className="grid md:grid-cols-2 gap-4 mt-2">
                  <div className="relative z-30">
                    <Label htmlFor="deviceType">Device Type *</Label>
                    <Select value={formData.deviceInfo.deviceType} onValueChange={(value) => handleInputChange('deviceInfo.deviceType', value)}>
                      <SelectTrigger className="bg-surface">
                        <SelectValue placeholder="Select device type" />
                      </SelectTrigger>
                      <SelectContent className="bg-surface z-50">
                        {deviceTypes.map(device => (
                          <SelectItem key={device.value} value={device.value}>
                            {device.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors['deviceInfo.deviceType'] && <p className="text-red-500 text-sm mt-1">{errors['deviceInfo.deviceType']}</p>}
                  </div>

                  <div className="relative z-20">
                    <Label htmlFor="operatingSystem">Operating System *</Label>
                    <Select value={formData.deviceInfo.operatingSystem} onValueChange={(value) => handleInputChange('deviceInfo.operatingSystem', value)}>
                      <SelectTrigger className="bg-surface">
                        <SelectValue placeholder="Select OS" />
                      </SelectTrigger>
                      <SelectContent className="bg-surface z-50">
                        {operatingSystems.map(os => (
                          <SelectItem key={os.value} value={os.value}>
                            {os.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors['deviceInfo.operatingSystem'] && <p className="text-red-500 text-sm mt-1">{errors['deviceInfo.operatingSystem']}</p>}
                  </div>

                  <div className="relative z-10">
                    <Label htmlFor="browser">Browser *</Label>
                    <Select value={formData.deviceInfo.browser} onValueChange={(value) => handleInputChange('deviceInfo.browser', value)}>
                      <SelectTrigger className="bg-surface">
                        <SelectValue placeholder="Select browser" />
                      </SelectTrigger>
                      <SelectContent className="bg-surface z-50">
                        {browsers.map(browser => (
                          <SelectItem key={browser.value} value={browser.value}>
                            {browser.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors['deviceInfo.browser'] && <p className="text-red-500 text-sm mt-1">{errors['deviceInfo.browser']}</p>}
                  </div>

                  <div>
                    <Label htmlFor="browserVersion">Browser Version</Label>
                    <Input
                      id="browserVersion"
                      placeholder="e.g., 91.0.4472.124"
                      value={formData.deviceInfo.browserVersion}
                      onChange={(e) => handleInputChange('deviceInfo.browserVersion', e.target.value)}
                      className="bg-surface"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="screenResolution">Screen Resolution</Label>
                  <Input
                    id="screenResolution"
                    placeholder="e.g., 1920x1080"
                    value={formData.deviceInfo.screenResolution}
                    onChange={(e) => handleInputChange('deviceInfo.screenResolution', e.target.value)}
                    className="bg-surface"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Report
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 